import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Plus, Trash2, UserCircle, Shield, Edit, Euro, Phone, StickyNote } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const errMsg = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(' · ');
  return fallback;
};

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [notesUser, setNotesUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    is_admin: false,
    role: '',
    hourly_rate: '',
    phone_number: '',
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    hourly_rate: '',
    phone_number: '',
  });
  const roleOf = (u) => (u.role === 'boekhouder' || u.role === 'esquarcio' ? u.role : u.is_admin ? 'admin' : 'employee');
  const ROLE_LABELS = { employee: 'Funcionário', admin: 'Administrador', boekhouder: 'Boekhouder (só leitura)', esquarcio: 'Esquarcio (só a 2ª empresa)' };
  const SPECIAL = ['boekhouder', 'esquarcio'];
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axiosInstance.post('/auth/register', { ...formData, is_admin: false, hourly_rate: parseFloat(formData.hourly_rate) || 0, phone_number: formData.phone_number || '' });
      if (formData.is_admin) {
        const list = await axiosInstance.get('/users');
        const created = (list.data || []).find((u) => u.email?.toLowerCase() === formData.email.toLowerCase());
        if (created) await axiosInstance.put(`/users/${created.id}`, { is_admin: true });
      }
      toast.success(formData.role === 'boekhouder' ? 'Boekhouder adicionado! Ele só pode ver e baixar Facturen, Inkoop e Uitgaven.' : formData.role === 'esquarcio' ? 'Conta Esquarcio adicionada! Só vê a área da Esquarcio.' : 'Funcionário adicionado!');
      setOpen(false);
      setFormData({ name: '', email: '', password: '', is_admin: false, role: '', hourly_rate: '', phone_number: '' });
      fetchUsers();
    } catch (error) {
      toast.error(errMsg(error, 'Erro ao adicionar funcionário'));
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name,
      email: user.email,
      password: '',
      hourly_rate: user.hourly_rate || '',
      phone_number: user.phone_number || '',
      is_admin: user.is_admin || false,
      role: roleOf(user),
    });
    setEditOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    const updateData = {};
    if (editFormData.name !== editingUser.name) updateData.name = editFormData.name;
    if (editFormData.email !== editingUser.email) updateData.email = editFormData.email;
    if (editFormData.password) updateData.password = editFormData.password;
    if (editFormData.role !== roleOf(editingUser)) {
      updateData.role = SPECIAL.includes(editFormData.role) ? editFormData.role : '';
      updateData.is_admin = editFormData.role === 'admin';
    }
    if (editFormData.hourly_rate !== editingUser.hourly_rate) {
      updateData.hourly_rate = parseFloat(editFormData.hourly_rate) || 0;
    }
    if (editFormData.phone_number !== editingUser.phone_number) {
      updateData.phone_number = editFormData.phone_number;
    }

    try {
      await axiosInstance.put(`/users/${editingUser.id}`, updateData);
      toast.success('Funcionário atualizado!');
      setEditOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(errMsg(error, 'Erro ao atualizar funcionário'));
    }
  };

  const handleToggleAdmin = async (user) => {
    const newStatus = !user.is_admin;
    const action = newStatus ? 'promover a Administrador' : 'remover de Administrador';
    if (!window.confirm(`Deseja ${action} "${user.name}"?`)) return;
    try {
      await axiosInstance.put(`/users/${user.id}`, { is_admin: newStatus });
      toast.success(`${user.name} ${newStatus ? 'promovido a Admin!' : 'removido de Admin!'}`);
      fetchUsers();
    } catch (error) {
      toast.error(errMsg(error, 'Erro ao alterar permissão'));
    }
  };

  const handleNotesOpen = (user) => {
    setNotesUser(user);
    setNotes(user.notes || '');
    setNotesOpen(true);
  };

  const handleSaveNotes = async () => {
    try {
      await axiosInstance.put(`/users/${notesUser.id}`, { notes });
      toast.success('Anotações salvas!');
      setNotesOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Erro ao salvar anotações');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja deletar este funcionário?')) return;

    try {
      await axiosInstance.delete(`/users/${id}`);
      toast.success('Funcionário deletado!');
      fetchUsers();
    } catch (error) {
      toast.error(errMsg(error, 'Erro ao deletar funcionário'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-800 text-lg">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-container">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-gray-800">Funcionários</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-user-button"
              className="bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold px-6 py-6 rounded-xl transition-all duration-300 hover:scale-[1.02]"
            >
              <Plus className="w-5 h-5 mr-2" />
              Adicionar Funcionário
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-md max-h-[90vh] overflow-y-auto" aria-describedby="new-user-description">
            <DialogHeader>
              <DialogTitle className="text-2xl text-gray-800">Novo Funcionário</DialogTitle>
              <p id="new-user-description" className="sr-only">Formulário para adicionar novo funcionário</p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-800 mb-2 block">Nome Completo</Label>
                <Input
                  data-testid="user-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="Nome do funcionário"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Email</Label>
                <Input
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="email@exemplo.com"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Senha</Label>
                <Input
                  data-testid="user-password-input"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp (com código do país)
                </Label>
                <Input
                  data-testid="user-phone-input"
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="+31612345678"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block flex items-center">
                  <Euro className="w-4 h-4 mr-2" />
                  Valor por Hora (€)
                </Label>
                <Input
                  data-testid="user-hourly-rate-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.hourly_rate}
                  onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="15.50"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Tipo de conta</Label>
                <select
                  data-testid="user-role-select"
                  value={formData.is_admin ? 'admin' : formData.role || 'employee'}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.value === 'admin', role: SPECIAL.includes(e.target.value) ? e.target.value : '' })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-800"
                >
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {formData.role === 'boekhouder' && (
                  <p className="text-xs text-[#1a5276] mt-1" data-testid="user-role-boekhouder-hint">Boekhouder: vê e baixa Facturen, Inkoop e Uitgaven (e o export), mas não pode alterar nada.</p>
                )}
                {formData.role === 'esquarcio' && (
                  <p className="text-xs text-[#0f3b57] mt-1" data-testid="user-role-esquarcio-hint">Esquarcio: vê e gere apenas a área da Esquarcio Schoonmaakbedrijf (faturas, notas, clientes). Nada da Cardoso.</p>
                )}
              </div>
              <Button
                type="submit"
                data-testid="user-submit-button"
                className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white py-6 rounded-xl"
              >
                Adicionar Funcionário
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-md max-h-[90vh] overflow-y-auto" aria-describedby="edit-user-description">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">Editar Funcionário</DialogTitle>
            <p id="edit-user-description" className="sr-only">Formulário para editar funcionário</p>
          </DialogHeader>
          {editingUser && (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <Label className="text-gray-800 mb-2 block">Nome Completo</Label>
                <Input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                  required
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block">Nova Senha (deixe vazio para manter)</Label>
                <Input
                  type="password"
                  value={editFormData.password}
                  onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block flex items-center">
                  <Phone className="w-4 h-4 mr-2" />
                  WhatsApp
                </Label>
                <Input
                  type="tel"
                  value={editFormData.phone_number}
                  onChange={(e) => setEditFormData({ ...editFormData, phone_number: e.target.value })}
                  className="bg-gray-50 border-white/30 text-gray-800"
                  placeholder="+31612345678"
                />
              </div>
              <div>
                <Label className="text-gray-800 mb-2 block flex items-center">
                  <Euro className="w-4 h-4 mr-2" />
                  Valor por Hora (€)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editFormData.hourly_rate}
                  onChange={(e) => setEditFormData({ ...editFormData, hourly_rate: e.target.value })}
                  className="bg-gray-50 border-white/30 text-gray-800"
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <Label className="text-gray-800 mb-2 block flex items-center gap-2"><Shield className="w-4 h-4 text-[#3da5a5]" /> Tipo de conta</Label>
                <select
                  data-testid="edit-role-select"
                  value={editFormData.role}
                  onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value, is_admin: e.target.value === 'admin' })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white text-gray-800"
                >
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white py-6 rounded-xl"
              >
                Salvar Alterações
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="bg-white shadow-sm border-gray-200 text-gray-800 max-w-md" aria-describedby="notes-description">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-800">Anotações - {notesUser?.name}</DialogTitle>
            <p id="notes-description" className="sr-only">Anotações sobre o funcionário</p>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Adicione anotações sobre este funcionário..."
              className="bg-gray-50 border-white/30 text-gray-800 min-h-[200px]"
            />
            <Button
              onClick={handleSaveNotes}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white py-6 rounded-xl"
            >
              Salvar Anotações
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map((user) => (
          <Card
            key={user.id}
            data-testid={`user-card-${user.id}`}
            className="bg-gray-50 backdrop-blur-xl border-gray-200 hover:bg-white/15 transition-all duration-200"
          >
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div className="flex items-start space-x-3 flex-1">
                <div className="p-3 rounded-full bg-gradient-to-br from-[#3da5a5] to-[#2d8585]">
                  {user.is_admin ? (
                    <Shield className="w-6 h-6 text-gray-800" />
                  ) : (
                    <UserCircle className="w-6 h-6 text-gray-800" />
                  )}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-gray-800 text-lg mb-1">{user.name}</CardTitle>
                  <p className="text-gray-500 text-sm">{user.email}</p>
                  {user.phone_number && (
                    <div className="flex items-center text-gray-400 text-xs mt-1">
                      <Phone className="w-3 h-3 mr-1" />
                      {user.phone_number}
                    </div>
                  )}
                  <span
                    data-testid={`user-role-badge-${user.id}`}
                    className={`inline-block px-2 py-1 rounded-full text-xs mt-2 ${
                      user.role === 'esquarcio'
                        ? 'bg-[#0f3b57] text-white'
                        : user.role === 'boekhouder'
                        ? 'bg-[#1a5276]/15 text-[#1a5276]'
                        : user.is_admin
                        ? 'bg-[#3da5a5]/20 text-[#3da5a5]'
                        : 'bg-gray-50 text-gray-500'
                    }`}
                  >
                    {user.role === 'esquarcio' ? 'Esquarcio' : user.role === 'boekhouder' ? 'Boekhouder (só leitura)' : user.is_admin ? 'Administrador' : 'Funcionário'}
                  </span>
                  {user.hourly_rate > 0 && (
                    <div className="flex items-center text-white/80 text-sm mt-2">
                      <Euro className="w-3 h-3 mr-1" />
                      <span>{user.hourly_rate.toFixed(2)}/hora</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col space-y-1">
                {!SPECIAL.includes(user.role) && (
                <Button
                  data-testid={`toggle-admin-${user.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleToggleAdmin(user)}
                  className={user.is_admin ? "text-orange-500 hover:text-orange-400 hover:bg-orange-50" : "text-[#3da5a5] hover:text-[#2d8585] hover:bg-[#3da5a5]/10"}
                  title={user.is_admin ? "Remover Admin" : "Promover a Admin"}
                >
                  <Shield className="w-4 h-4" />
                </Button>
                )}
                <Button
                  data-testid={`notes-user-${user.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleNotesOpen(user)}
                  className="text-gray-800 hover:text-[#3da5a5] hover:bg-gray-100"
                  title="Anotações"
                >
                  <StickyNote className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`edit-user-${user.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(user)}
                  className="text-gray-800 hover:text-[#3da5a5] hover:bg-gray-100"
                  title="Editar"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button
                  data-testid={`delete-user-${user.id}`}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(user.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-400/20"
                  title="Deletar"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default Users;